import { X } from "lucide-react";
import type { ReactNode } from "react";

import {
  Dialog,
  DialogClose,
  DialogContent,
  DialogDescription,
  DialogTitle,
} from "../../../../../components/ui/dialog";
import { closeCalendarOverlay, isCalendarOverlayOpen } from "../../../../../lib/calendarOverlay";
import { useDraggableDialog } from "./useDraggableDialog";

// ---------------------------------------------------------------------------
// CallScriptDialogShell
//
// The outer chrome both Call Script modals share: a wide, drag-by-the-header,
// deliberately non-dismissable dialog.
//
// Three behaviours worth knowing. The latter two are workarounds the
// individual screens used to carry their own copies of:
//
//   * Modal mode — left at Radix's default, so the dialog renders a scrim.
//     This was previously `modal={false}`, because uikit's DatePicker
//     portals its calendar from its own bundled copy of Radix and inherited
//     the `pointer-events: none` our copy puts on <body>, leaving day cells
//     unclickable. But `modal={false}` makes Radix's DialogOverlay return null
//     outright (`context.modal ? <Presence/> : null` — `forceMount` does not
//     override it), so the dialog had no scrim and the page behind stayed
//     fully interactive.
//
//     The calendar is exempted in CSS instead: see the
//     `[data-slot="popover-content"]:has([data-slot="calendar"])` rule in
//     styles/uikitInputs.css.
//   * `onInteractOutside` is prevented — a click outside never discards a
//     call in progress.
//   * Escape belongs to the calendar while one is open (see calendarOverlay);
//     otherwise both layers would dismiss on the same keypress, taking the
//     whole script and everything typed into it down with the calendar.
// ---------------------------------------------------------------------------

interface CallScriptDialogShellProperties {
  readonly open: boolean;
  readonly onOpenChange: (open: boolean) => void;
  readonly title: ReactNode;
  /** Screen-reader-only description of the dialog. */
  readonly description: string;
  readonly closeLabel: string;
  /** Identifies the drag handle in tests. */
  readonly headerTestId: string;
  /**
   * Everything below the header. Deliberately unstructured: the employment
   * script swaps between a loading skeleton, a retryable error and the form,
   * and owns its own DialogFooter, so the shell imposes no body layout.
   */
  readonly children: ReactNode;
}

export function CallScriptDialogShell({
  open,
  onOpenChange,
  title,
  description,
  closeLabel,
  headerTestId,
  children,
}: CallScriptDialogShellProperties) {
  const { offset, onHeaderPointerDown } = useDraggableDialog(open);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        showCloseButton={false}
        onInteractOutside={(event) => event.preventDefault()}
        onEscapeKeyDown={(event) => {
          if (isCalendarOverlayOpen()) {
            event.preventDefault();
            closeCalendarOverlay();
          }
        }}
        style={{ translate: `calc(-50% + ${offset.x}px) calc(-50% + ${offset.y}px)` }}
        className="gap-(--spacing-0) p-(--spacing-0) overflow-hidden sm:max-w-3xl lg:max-w-5xl"
      >
        <div
          data-testid={headerTestId}
          onPointerDown={onHeaderPointerDown}
          className="bg-primary text-primary-foreground px-(--spacing-4) py-(--spacing-2) flex cursor-grab touch-none select-none items-center justify-between border-b active:cursor-grabbing"
        >
          <DialogTitle className="text-primary-foreground text-(length:--typography-base-sizes-extra-small-font-size)/(--typography-base-sizes-extra-small-line-height) font-(--font-weight-semibold)">
            {title}
          </DialogTitle>
          <DialogDescription className="sr-only">{description}</DialogDescription>
          <DialogClose
            className="text-primary-foreground/80 hover:text-primary-foreground p-(--spacing-1) transition-colors"
            aria-label={closeLabel}
          >
            <X className="size-4.5" aria-hidden="true" />
          </DialogClose>
        </div>

        {children}
      </DialogContent>
    </Dialog>
  );
}
